import { api } from '@web/api/client'
import type {
  IdentityState,
  PermissionMode,
  UserPreferences,
} from '@web/api/types'
import { clearSessionStores } from '@web/state/session-store'
import { clearWorkspaceStates } from '@web/state/workspace-state'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

/** In-app navigation records its depth so a screen can tell whether "back" stays inside Canopy. */
export function navigateWorkspace(path: string) {
  window.history.pushState(
    { depth: (window.history.state?.depth ?? 0) + 1 },
    '',
    path,
  )
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** Return to the previous in-app screen, or to `fallback` when this is the first one. */
export function navigateBack(fallback: string) {
  if ((window.history.state?.depth ?? 0) > 0) window.history.back()
  else navigateWorkspace(fallback)
}

function contextKey(state: IdentityState | null) {
  return JSON.stringify({
    user: state?.current_user?.id ?? null,
    organization: state?.current_org?.id ?? null,
    superuser: state?.current_user?.is_superuser ?? false,
    directoryAdmin: state?.can_manage_directory ?? false,
    monitoringAdmin: state?.can_monitor ?? false,
    permissions: [...(state?.permissions ?? [])].sort(),
    applications: [...(state?.allowed_apps ?? [])].sort(),
    configuration: state?.app_configs ?? {},
  })
}

function clearWorkspace() {
  clearSessionStores()
  clearWorkspaceStates()
  for (const key of Object.keys(localStorage)) {
    if (
      (key.startsWith('bridge.app.') && key.endsWith('.binding')) ||
      key === 'bridge.greeting-binding'
    ) {
      localStorage.removeItem(key)
    }
  }
}

type DesktopSsoPending = { email: string; expiresAt: number }
type DesktopSsoResult =
  | { status: 'authenticated' }
  | { status: 'link_pending' }
  | { status: 'no_account'; email: string; adminContact: string | null }
  | {
      status: 'email_mismatch'
      expectedEmail: string
      actualEmail: string
    }
  | { status: 'error'; message: string }
export type SsoCallbackFailure =
  | {
      status: 'no_account'
      email: string | null
      adminContact: string | null
    }
  | {
      status: 'email_mismatch'
      expectedEmail: string | null
      actualEmail: string | null
    }
  | { status: 'link_pending' | 'error' }

function callbackEmail(value: string | null): string | null {
  const email = value?.trim().toLowerCase()
  if (!email || email.length > 254) return null
  return /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/.test(email) ? email : null
}

function callbackFailure(url: URL): SsoCallbackFailure | null {
  const status = url.searchParams.get('sso_status')
  if (status === 'no_account')
    return {
      status,
      email: callbackEmail(url.searchParams.get('sso_email')),
      adminContact: callbackEmail(url.searchParams.get('sso_admin_contact')),
    }
  if (status === 'email_mismatch')
    return {
      status,
      expectedEmail: callbackEmail(url.searchParams.get('sso_expected_email')),
      actualEmail: callbackEmail(url.searchParams.get('sso_actual_email')),
    }
  if (status === 'link_pending' || status === 'error') return { status }
  return null
}

type DesktopSsoBridge = {
  startSso: (email: string) => Promise<DesktopSsoPending>
  cancelSso: () => Promise<void>
  pendingSso: () => Promise<DesktopSsoPending | null>
  onSsoResult: (listener: (result: DesktopSsoResult) => void) => () => void
}

function desktopSsoBridge(): DesktopSsoBridge | null {
  if (document.documentElement.dataset.desktop !== 'true') return null
  return (
    (window as unknown as { canopyDesktop?: DesktopSsoBridge }).canopyDesktop ??
    null
  )
}

type IdentityContextValue = {
  state: IdentityState | null
  loading: boolean
  busy: boolean
  savingPreferences: boolean
  error: string
  ssoNotice: string
  ssoFailure: SsoCallbackFailure | null
  ssoPending: DesktopSsoPending | null
  desktopSsoAvailable: boolean
  addingAccount: boolean
  workspaceKey: string
  reload: () => Promise<void>
  savePreferences: (mode: PermissionMode | UserPreferences) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  switchUser: (userId: string) => Promise<void>
  switchOrganization: (orgId: string) => Promise<void>
  logout: (all?: boolean) => Promise<void>
  addAccount: () => void
  cancelAddAccount: () => void
  dismissError: () => void
  dismissSsoNotice: () => void
  retrySsoWithDifferentAccount: () => void
  startDesktopSso: (email: string) => Promise<void>
  cancelDesktopSso: () => Promise<void>
}

const IdentityContext = createContext<IdentityContextValue | null>(null)

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<IdentityState | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [savingPreferences, setSavingPreferences] = useState(false)
  const [error, setError] = useState('')
  const [ssoError, setSsoError] = useState('')
  const [ssoNotice, setSsoNotice] = useState('')
  const [ssoFailure, setSsoFailure] = useState<SsoCallbackFailure | null>(null)
  const [ssoPending, setSsoPending] = useState<DesktopSsoPending | null>(null)
  const [addingAccount, setAddingAccount] = useState(false)
  const [revision, setRevision] = useState(0)
  const current = useRef<IdentityState | null>(null)
  const mutation = useRef(false)
  const requestGeneration = useRef(0)
  const refreshRequest = useRef<Promise<void> | null>(null)
  const bootstrapRequest = useRef<Promise<IdentityState> | null>(null)
  const ssoResultGeneration = useRef(0)

  const applyState = useCallback((next: IdentityState, reset: boolean) => {
    if (reset || contextKey(current.current) !== contextKey(next)) {
      clearWorkspace()
      setRevision((value) => value + 1)
      if (reset || current.current) navigateWorkspace('/')
    }
    current.current = next
    setState(next)
  }, [])

  const reload = useCallback(async () => {
    if (mutation.current) return
    if (refreshRequest.current) return refreshRequest.current
    if (!current.current) setLoading(true)
    const generation = requestGeneration.current
    const request = (async () => {
      try {
        const next = await api.identity()
        if (!mutation.current && generation === requestGeneration.current) {
          applyState(next, false)
          setError('')
        }
      } catch (reason) {
        if (!mutation.current && generation === requestGeneration.current)
          setError((reason as Error).message)
      } finally {
        setLoading(false)
        refreshRequest.current = null
      }
    })()
    refreshRequest.current = request
    return request
  }, [applyState])

  useEffect(() => {
    let mounted = true
    const generation = requestGeneration.current
    const callbackUrl = new URL(window.location.href)
    const ssoStatus = callbackUrl.searchParams.get('sso_status')
    if (ssoStatus) {
      setSsoFailure(callbackFailure(callbackUrl))
      for (const key of [
        'sso_status',
        'sso_email',
        'sso_admin_contact',
        'sso_expected_email',
        'sso_actual_email',
      ])
        callbackUrl.searchParams.delete(key)
      window.history.replaceState(
        window.history.state,
        '',
        callbackUrl.pathname + callbackUrl.search + callbackUrl.hash,
      )
    }
    if (!bootstrapRequest.current) {
      bootstrapRequest.current = (async () => {
        const token = new URLSearchParams(location.hash.slice(1)).get('access')
        if (token) {
          history.replaceState(null, '', location.pathname + location.search)
          await api.unlock(token)
        }
        return api.identity()
      })()
    }
    void bootstrapRequest.current
      .then((next) => {
        if (mounted && generation === requestGeneration.current)
          applyState(next, false)
      })
      .catch((reason) => {
        if (mounted) setError((reason as Error).message)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    const refresh = () => void reload()
    const accessLink = () => {
      const token = new URLSearchParams(location.hash.slice(1)).get('access')
      if (!token) return
      history.replaceState(null, '', location.pathname + location.search)
      void api
        .unlock(token)
        .then(() => reload())
        .catch((reason) => {
          if (mounted) setError((reason as Error).message)
        })
    }
    const changed = (event: StorageEvent) => {
      if (event.key === 'bridge.identity-changed') refresh()
    }
    const visible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    window.addEventListener('bridge:identity-expired', refresh)
    window.addEventListener('hashchange', accessLink)
    window.addEventListener('focus', refresh)
    window.addEventListener('storage', changed)
    document.addEventListener('visibilitychange', visible)
    return () => {
      mounted = false
      window.removeEventListener('bridge:identity-expired', refresh)
      window.removeEventListener('hashchange', accessLink)
      window.removeEventListener('focus', refresh)
      window.removeEventListener('storage', changed)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [applyState, reload])

  useEffect(() => {
    const desktop = desktopSsoBridge()
    if (!desktop) return
    let mounted = true
    let receivedResult = false
    const off = desktop.onSsoResult((result) => {
      ssoResultGeneration.current += 1
      receivedResult = true
      setSsoPending(null)
      if (result.status === 'authenticated') {
        const generation = ++requestGeneration.current
        mutation.current = true
        setBusy(true)
        void api
          .identity()
          .then((next) => {
            if (generation !== requestGeneration.current) return
            if (!next.current_user)
              throw new Error(
                'Canopy could not retain your Google sign-in. Please try again.',
              )
            applyState(next, false)
            setError('')
            setSsoError('')
          })
          .catch((reason) => {
            if (generation === requestGeneration.current)
              setSsoError((reason as Error).message)
          })
          .finally(() => {
            mutation.current = false
            setBusy(false)
            setLoading(false)
          })
      } else if (result.status === 'link_pending') {
        setSsoError('')
        setSsoNotice(
          'Your Google account needs an administrator’s approval. Try signing in again after the account link is approved.',
        )
      } else if (
        result.status === 'no_account' ||
        result.status === 'email_mismatch'
      ) {
        setError('')
        setSsoError('')
        setSsoNotice('')
        setSsoFailure(result)
      } else {
        setSsoError(result.message)
      }
    })
    void desktop
      .pendingSso()
      .then((pending) => {
        if (mounted && !receivedResult) setSsoPending(pending)
      })
      .catch((reason) => {
        if (mounted) setError((reason as Error).message)
      })
    return () => {
      mounted = false
      off()
    }
  }, [applyState])

  useEffect(() => {
    if (!ssoPending) return
    const timeout = setTimeout(
      () => {
        setSsoPending(null)
        setError('Google sign-in expired. Please try again.')
      },
      Math.max(0, ssoPending.expiresAt - Date.now()),
    )
    return () => clearTimeout(timeout)
  }, [ssoPending])

  const change = useCallback(
    async (operation: () => Promise<IdentityState>) => {
      if (mutation.current) return
      mutation.current = true
      requestGeneration.current += 1
      setBusy(true)
      setError('')
      setSsoError('')
      clearWorkspace()
      try {
        const next = await operation()
        applyState(next, true)
        setAddingAccount(false)
        localStorage.setItem(
          'bridge.identity-changed',
          `${Date.now()}:${Math.random()}`,
        )
      } catch (reason) {
        setError((reason as Error).message)
        throw reason
      } finally {
        mutation.current = false
        setBusy(false)
      }
    },
    [applyState],
  )

  const savePreferences = useCallback(
    async (mode: PermissionMode | UserPreferences) => {
      if (mutation.current) return
      mutation.current = true
      requestGeneration.current += 1
      setSavingPreferences(true)
      setBusy(true)
      setError('')
      try {
        applyState(await api.savePreferences(mode), false)
        localStorage.setItem('bridge.identity-changed', String(Date.now()))
      } catch (reason) {
        setError((reason as Error).message)
        throw reason
      } finally {
        mutation.current = false
        setSavingPreferences(false)
        setBusy(false)
      }
    },
    [applyState],
  )

  return (
    <IdentityContext.Provider
      value={{
        state,
        loading,
        busy,
        savingPreferences,
        error: ssoError || error,
        ssoNotice,
        ssoFailure,
        ssoPending,
        desktopSsoAvailable: desktopSsoBridge() !== null,
        addingAccount,
        workspaceKey: `${contextKey(state)}:${revision}`,
        reload,
        savePreferences,
        login: (email, password) => change(() => api.login(email, password)),
        switchUser: (user_id) => change(() => api.setIdentity({ user_id })),
        switchOrganization: (org_id) =>
          change(() => api.setIdentity({ org_id })),
        logout: (all = false) => change(() => api.logout({ all })),
        addAccount: () => {
          setAddingAccount(true)
          setError('')
        },
        cancelAddAccount: () => {
          setAddingAccount(false)
          setError('')
        },
        dismissError: () => {
          setError('')
          setSsoError('')
        },
        dismissSsoNotice: () => setSsoNotice(''),
        retrySsoWithDifferentAccount: () => {
          setSsoFailure(null)
          setSsoNotice('')
          setError('')
          setSsoError('')
          setAddingAccount(Boolean(current.current?.current_user))
          navigateWorkspace('/')
        },
        startDesktopSso: async (email) => {
          const desktop = desktopSsoBridge()
          if (!desktop)
            throw new Error('The desktop sign-in bridge is unavailable.')
          setError('')
          setSsoError('')
          setSsoNotice('')
          const generation = ssoResultGeneration.current
          const pending = await desktop.startSso(email)
          if (generation === ssoResultGeneration.current) setSsoPending(pending)
        },
        cancelDesktopSso: async () => {
          const desktop = desktopSsoBridge()
          if (!desktop) return
          await desktop.cancelSso()
          setSsoPending(null)
          setError('')
          setSsoError('')
          setSsoNotice('')
        },
      }}
    >
      {children}
    </IdentityContext.Provider>
  )
}

export function useIdentity() {
  const value = useContext(IdentityContext)
  if (!value) throw new Error('IdentityProvider is required.')
  return value
}
